import { isNewMetric } from '@/lib/metric-utils';
import type { TextProps } from '@chakra-ui/react';
import { Badge, FormatNumber, Span, Text } from '@chakra-ui/react';
import type { MetricTrend } from '@fluenceur/types';
import { ArrowDown, ArrowDownRight, ArrowUp, ArrowUpRight } from 'iconoir-react';
import type { ReactNode } from 'react';
import { type TrendPolarity, type TrendTone, trendTone } from './trend-tone';

// green.700/red.700 keep AA contrast for trend text on cream / off-white cards.
const TEXT_COLOR: Record<TrendTone, string> = {
  good: 'green.700',
  bad: 'red.700',
  neutral: 'fg.muted',
};

// The pill reads through the theme Badge recipe: a palette per tone, never a colour.
const PILL_PALETTE: Record<TrendTone, 'green' | 'red' | 'gray'> = {
  good: 'green',
  bad: 'red',
  neutral: 'gray',
};

interface TrendBadgeProps extends TextProps {
  trend?: MetricTrend;
  percentageChange?: number;
  useFormatNumber?: boolean;
  /** Text after the percentage, in a neutral colour ("+6K followers"). */
  label?: ReactNode;
  /** A decorative arrow before the percentage, pointing the way it moved; none when flat. */
  showArrow?: boolean;
  /** Formats the unsigned size of the change when the caller owns the number style ("26,1%"). */
  formatPercentage?: (magnitude: number) => string;
  /**
   * Opt-in: colour by what the change means rather than by its direction (a rising
   * CPA is red). Without it a rise is green and a fall red, as everywhere else.
   */
  polarity?: TrendPolarity;
  /** `pill`: a tinted chip with a diagonal arrow ("↗ +12%"), for stat cards. */
  appearance?: 'text' | 'pill';
}

/**
 * Displays trend as simple colored text: "+34%" or "-12%"
 * Returns null when there's no meaningful comparison (e.g. previous value was 0)
 *
 * With `showArrow` or `label` it reads "↑ +26.1% +6K followers": the arrow and the
 * percentage carry the direction colour, the label stays neutral. The colour
 * states a direction, never a verdict, unless the caller opts into a `polarity`.
 */
export const TrendBadge: React.FC<TrendBadgeProps> = ({
  trend,
  percentageChange,
  useFormatNumber = false,
  label,
  showArrow = false,
  formatPercentage,
  polarity,
  appearance = 'text',
  ...props
}) => {
  if (isNewMetric(trend)) return null;

  const direction = trend
    ? trend.direction
    : percentageChange !== undefined
      ? percentageChange > 0
        ? 'up'
        : percentageChange < 0
          ? 'down'
          : 'flat'
      : 'flat';

  const percentage = trend ? trend.percentage : percentageChange || 0;
  const tone = trendTone(direction, polarity);
  const color = TEXT_COLOR[tone];
  const sign = direction === 'up' ? '+' : direction === 'down' ? '-' : '';

  const magnitude = useFormatNumber ? (
    <FormatNumber value={Math.abs(percentage) / 100} style="percent" maximumFractionDigits={1} />
  ) : formatPercentage ? (
    formatPercentage(Math.abs(percentage))
  ) : (
    `${Math.abs(percentage)}%`
  );

  const toneAttributes = { 'data-testid': 'trend-badge', 'data-tone': tone };

  if (appearance === 'pill') {
    const PillArrow =
      direction === 'up' ? ArrowUpRight : direction === 'down' ? ArrowDownRight : null;
    return (
      <Badge
        variant="subtle"
        colorPalette={PILL_PALETTE[tone]}
        data-casing="word"
        css={{ fontVariantNumeric: 'tabular-nums' }}
        {...toneAttributes}
      >
        {PillArrow && <PillArrow width={12} height={12} strokeWidth={2} aria-hidden="true" />}
        <Span>
          {sign}
          {magnitude}
        </Span>
      </Badge>
    );
  }

  if (!showArrow && label === undefined) {
    return (
      <Text fontSize="xs" fontWeight="medium" color={color} {...toneAttributes} {...props}>
        {useFormatNumber ? (
          <>
            {sign}
            {magnitude}
          </>
        ) : (
          `${sign}${magnitude}`
        )}
      </Text>
    );
  }

  const Arrow = direction === 'up' ? ArrowUp : direction === 'down' ? ArrowDown : null;

  return (
    <Text
      display="inline-flex"
      alignItems="center"
      flexWrap="wrap"
      columnGap="1.5"
      fontSize="xs"
      fontWeight="medium"
      color={color}
      {...toneAttributes}
      {...props}
    >
      <Span display="inline-flex" alignItems="center" gap="1">
        {showArrow && Arrow && <Arrow width={12} height={12} aria-hidden="true" />}
        <Span>
          {sign}
          {magnitude}
        </Span>
      </Span>
      {label !== undefined && (
        <Span fontFamily="body" fontSize="0.8em" fontWeight="normal" color="fg.muted">
          {label}
        </Span>
      )}
    </Text>
  );
};
